import React, { useState, useMemo, useEffect } from 'react';
import MainLayout from '../../../shared/layouts/MainLayout';
import { Card, Alert, Badge, Loading, Spinner } from '../../../shared/components';
import { useReports } from '../hooks/useReports';
import { useExport } from '../hooks/useExport';
import ExportButtons from '../components/ExportButtons';
import ReportTable from '../components/ReportTable';
import ReportFilters from '../components/ReportFilters';

const StockReportPage = () => {
  const [filters, setFilters] = useState({
    search: '',
    expiryStatus: 'all',
    stockStatus: 'all'
  });
  
  const { data, loading, error, refetch } = useReports('stock', filters);
  const exportUtils = useExport();

  // Debug: Log the actual data structure
  useEffect(() => {
    console.log('StockReportPage - Full data from API:', data);
    if (data) {
      console.log('StockReportPage - data.data:', data.data);
      console.log('StockReportPage - data.data.data:', data.data?.data);
      console.log('StockReportPage - Is data.data.data an array?', Array.isArray(data.data?.data));
    }
  }, [data]);

  // Extract the actual data array from the nested structure
  const getRawDataArray = () => {
    if (!data) return [];
    
    // Based on your API response structure: { success: true, data: { type, filters, data: [...] } }
    if (data.data && data.data.data && Array.isArray(data.data.data)) {
      return data.data.data;
    }
    
    // Fallback: try data.data if it's an array
    if (data.data && Array.isArray(data.data)) {
      return data.data;
    }
    
    // Fallback: try data itself if it's an array
    if (Array.isArray(data)) {
      return data;
    }
    
    return [];
  };

  const rawData = getRawDataArray();

  // Apply local filters
  const filteredData = useMemo(() => {
    if (!rawData.length) return [];
    
    return rawData.filter(item => {
      // Search filter
      if (filters.search && !item.name.toLowerCase().includes(filters.search.toLowerCase())) {
        return false;
      }
      
      // Stock status filter
      if (filters.stockStatus !== 'all') {
        const isLowStock = item.total_stock <= item.minimum_stock;
        if (filters.stockStatus === 'low' && !isLowStock) return false;
        if (filters.stockStatus === 'adequate' && isLowStock) return false;
      }
      
      // Expiry status filter
      if (filters.expiryStatus !== 'all') {
        const today = new Date();
        const expiryDate = new Date(item.nearest_expiry);
        const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
        
        if (filters.expiryStatus === 'expired' && daysUntilExpiry > 0) return false;
        if (filters.expiryStatus === 'critical' && (daysUntilExpiry > 30 || daysUntilExpiry <= 0)) return false;
        if (filters.expiryStatus === 'warning' && (daysUntilExpiry > 90 || daysUntilExpiry <= 30)) return false;
        if (filters.expiryStatus === 'good' && daysUntilExpiry <= 90) return false;
      }
      
      return true;
    });
  }, [rawData, filters]);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalItems = rawData.length;
    const lowStock = rawData.filter(item => item.total_stock <= item.minimum_stock).length;
    const expiredStock = rawData.filter(item => parseInt(item.expired_stock) > 0).length;
    
    const today = new Date();
    const expiringSoon = rawData.filter(item => {
      const expiryDate = new Date(item.nearest_expiry);
      const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
      return daysUntilExpiry > 0 && daysUntilExpiry <= 30;
    }).length;
    
    return { totalItems, lowStock, expiredStock, expiringSoon };
  }, [rawData]);

  const columns = [
    { 
      header: 'Medicine Name', 
      accessor: 'name',
      render: (value) => <span className="font-medium">{value}</span>
    },
    { 
      header: 'Total Stock', 
      accessor: 'total_stock',
      render: (value, row) => (
        <span className={row.total_stock <= row.minimum_stock ? 'text-orange-600 font-semibold' : ''}>
          {value}
        </span>
      )
    },
    { 
      header: 'Minimum Stock', 
      accessor: 'minimum_stock',
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <span>{value}</span>
          {row.total_stock <= row.minimum_stock && (
            <Badge variant="warning">Low Stock</Badge>
          )}
        </div>
      )
    },
    { 
      header: 'Nearest Expiry', 
      accessor: 'nearest_expiry',
      render: (value) => {
        if (!value) return '-';
        
        const expiryDate = new Date(value);
        const today = new Date();
        const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
        
        let badgeVariant = 'success';
        let badgeText = 'Good';
        
        if (daysUntilExpiry <= 0) {
          badgeVariant = 'danger';
          badgeText = 'Expired';
        } else if (daysUntilExpiry <= 30) {
          badgeVariant = 'danger';
          badgeText = 'Critical';
        } else if (daysUntilExpiry <= 90) {
          badgeVariant = 'warning';
          badgeText = 'Warning';
        }
        
        return (
          <div className="flex items-center gap-2">
            <span>{new Date(value).toLocaleDateString()}</span>
            {daysUntilExpiry <= 90 && (
              <Badge variant={badgeVariant}>
                {badgeText} {daysUntilExpiry > 0 && `(${daysUntilExpiry} days)`}
              </Badge>
            )}
          </div>
        );
      }
    },
    { 
      header: 'Expired Stock', 
      accessor: 'expired_stock',
      render: (value) => {
        const numValue = parseInt(value);
        return numValue > 0 ? (
          <Badge variant="danger">{value} units</Badge>
        ) : (
          <span className="text-gray-500">0</span>
        );
      }
    }
  ];

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    // Only call refetch if needed (if filters affect backend)
    // refetch();
  };

  const handleReset = () => {
    setFilters({
      search: '',
      expiryStatus: 'all',
      stockStatus: 'all'
    });
  };

  // Handler functions for exports
  const handleCSVExport = () => {
    exportUtils.exportToCSV(filteredData, 'stock_report');
  };

  const handleExcelExport = () => {
    exportUtils.exportToExcel(filteredData, 'stock_report');
  };

  const handlePDFExport = () => {
    exportUtils.exportToPDF(
      filteredData, 
      columns, 
      'Stock Availability Report', 
      'stock_report'
    );
  };

  const handlePrint = () => {
    exportUtils.print();
  };

  if (loading) return <Spinner />;
  if (error) return <Alert type="error" message={error} />;

  return (
    // <MainLayout>
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-6">Stock Availability Report</h1>
        
        {/* Filters */}
        <ReportFilters
          filters={filters}
          onApply={handleApplyFilters}
          onReset={handleReset}
          showDateRange={false}
        />
        
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <div className="p-4">
              <h3 className="text-sm font-medium text-gray-500">Total Medicines</h3>
              <p className="text-2xl font-bold text-blue-600">{stats.totalItems}</p>
            </div>
          </Card>
          <Card>
            <div className="p-4">
              <h3 className="text-sm font-medium text-gray-500">Low Stock Items</h3>
              <p className="text-2xl font-bold text-orange-600">{stats.lowStock}</p>
            </div>
          </Card>
          <Card>
            <div className="p-4">
              <h3 className="text-sm font-medium text-gray-500">Expiring Soon (≤30 days)</h3>
              <p className="text-2xl font-bold text-yellow-600">{stats.expiringSoon}</p>
            </div>
          </Card>
          <Card>
            <div className="p-4">
              <h3 className="text-sm font-medium text-gray-500">With Expired Stock</h3>
              <p className="text-2xl font-bold text-red-600">{stats.expiredStock}</p>
            </div>
          </Card>
        </div>
        
        {/* Export Buttons */}
        <ExportButtons
          onExportCSV={handleCSVExport}
          onExportExcel={handleExcelExport}
          onExportPDF={handlePDFExport}
          onPrint={handlePrint}
          disabled={loading}
          showRecordCount={true}
          recordCount={filteredData.length}
        />
        
        {/* Main Table */}
        <Card>
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Current Stock Status</h2>
              {filters.search && (
                <span className="text-sm text-gray-500">
                  Filtered by: "{filters.search}"
                </span>
              )}
            </div>
            <ReportTable
              data={{ data: filteredData }}
              columns={columns}
            />
            
            {/* No Data Message */}
            {filteredData.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <p className="text-lg">No stock data available</p>
                <p className="text-sm mt-2">Try adjusting your filters</p>
              </div>
            )}
          </div>
        </Card>
      </div>
    // </MainLayout>
  );
};

export default StockReportPage;